// WebSocket szolgáltatás real-time chat kommunikációhoz

const WS_URL = process.env.REACT_APP_WS_URL;

if (!WS_URL) {
  console.error('REACT_APP_WS_URL environment variable is not set');
}

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectInterval = 1000; // Kezdeti várakozás 1 másodperc
    this.maxReconnectInterval = 15000; // Maximum 15 másodperc
    this.isReconnecting = false;
    this.messageHandlers = new Set();
    this.connectionHandlers = new Set();
    this.isManualClose = false;
  }

  // Csatlakozás a WebSocket szerverhez
  async connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    

    this.isManualClose = false;

    return new Promise((resolve, reject) => {
      try {
        if (!WS_URL) {
          reject(new Error('WebSocket URL is not configured'));
          return;
        }
        
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          
          this.reconnectAttempts = 0;
          this.reconnectInterval = 1000;
          this.isReconnecting = false;
          this.notifyConnectionHandlers('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            if (!event.data || typeof event.data !== 'string') {
              console.warn('Invalid WebSocket message format');
              return;
            }
            
            const data = JSON.parse(event.data);
            
            if (!data || typeof data !== 'object') {
              console.warn('Invalid WebSocket message data');
              return;
            }
            
            this.notifyMessageHandlers(data);
          } catch (error) {
            console.warn('WebSocket message parsing failed');
          }
        };

        this.ws.onclose = (event) => {
          
          this.notifyConnectionHandlers('disconnected');
          
          if (!this.isManualClose && this.shouldReconnect()) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
         
          if (this.reconnectAttempts === 0) {
            console.warn('WebSocket kapcsolódás sikertelen (szerver lehet hogy nincs elindítva)');
          } else {
            console.error('WebSocket hiba:', error);
          }
          this.notifyConnectionHandlers('error', error);
          reject(error);
        };

        // Timeout a kapcsolódáshoz
        setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            this.ws.close();
            reject(new Error('WebSocket kapcsolódás timeout'));
          }
        }, 5000);

      } catch (error) {
        reject(error);
      }
    });
  }

  // Kapcsolat bontása
  disconnect() {
    this.isManualClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // Üzenet küldése
  sendMessage(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = {
        timestamp: new Date().toISOString(),
        ...data
      };
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
      return false;
    }
  }

  // Csatlakozás chat szobához
  joinChat(chatId, userId, userName) {
    return this.sendMessage({
      type: 'join_chat',
      chat_id: chatId,
      user_id: userId,
      user_name: userName
    });
  }

  // Kilépés chat szobából
  leaveChat(chatId, userId, userName) {
    return this.sendMessage({
      type: 'leave_chat',
      chat_id: chatId,
      user_id: userId,
      user_name: userName
    });
  }

  // Chat üzenet küldése (új protokoll)
  sendChatMessage(chatId, userId, userName, message, messageType = 'text') {
    return this.sendMessage({
      type: 'send_message',
      chat_id: chatId,
      user_id: userId,
      user_name: userName,
      message: message,
      message_type: messageType
    });
  }

  // Typing jelzés küldése (új protokoll)
  sendTypingStatus(chatId, isTyping) {
    return this.sendMessage({
      type: isTyping ? 'typing_start' : 'typing_stop',
      chat_id: chatId
    });
  }

  // Üzenet handler hozzáadása
  addMessageHandler(handler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  // Kapcsolat státusz handler hozzáadása
  addConnectionHandler(handler) {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  // Üzenet handlerek értesítése
  notifyMessageHandlers(data) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Üzenet handler hiba:', error);
      }
    });
  }

  // Kapcsolat handlerek értesítése
  notifyConnectionHandlers(status, error = null) {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(status, error);
      } catch (error) {
        console.error('Kapcsolat handler hiba:', error);
      }
    });
  }

  // Újracsatlakozás ütemezése exponenciális backoff-fal
  scheduleReconnect() {
    if (this.isReconnecting || this.isManualClose) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      console.error('Maximális újracsatlakozási kísérletek elérve');
      this.notifyConnectionHandlers('failed');
      return;
    }

    const delay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectInterval
    );


    this.notifyConnectionHandlers('reconnecting', { attempt: this.reconnectAttempts, delay });

    setTimeout(() => {
      if (!this.isManualClose) {
        this.connect().catch(error => {
          console.error('Újracsatlakozási hiba:', error);
          this.scheduleReconnect();
        });
      }
    }, delay);
  }

  // Újracsatlakozás szükséges-e
  shouldReconnect() {
    return this.reconnectAttempts < this.maxReconnectAttempts && !this.isManualClose;
  }



  // Kapcsolat státuszának lekérése
  getConnectionStatus() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return this.isReconnecting ? 'reconnecting' : 'disconnected';
      default:
        return 'unknown';
    }
  }

  // Manuális újracsatlakozás
  reconnect() {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    return this.connect();
  }
}

// Singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;
